import { useState } from 'react';
import type { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';

import {
  associateSoftwareToken as associateSoftwareTokenWithAccessToken,
  setSoftwareTokenMfa,
  verifySoftwareToken as verifySoftwareTokenWithAccessToken,
} from '../cognito-user-api';

function currentCognitoUser(pool: CognitoUserPool | undefined): CognitoUser {
  const user = pool?.getCurrentUser();
  if (!user) throw new Error('Sign in with your email and password before configuring MFA.');
  return user;
}

function establishSession(user: CognitoUser): Promise<void> {
  return new Promise((resolve, reject) =>
    user.getSession((error: Error | null) => (error ? reject(error) : resolve())),
  );
}

function associateToken(user: CognitoUser): Promise<string> {
  return new Promise((resolve, reject) =>
    user.associateSoftwareToken({
      associateSecretCode: resolve,
      onFailure: reject,
    }),
  );
}

function verifyToken(user: CognitoUser, code: string): Promise<void> {
  return new Promise((resolve, reject) =>
    user.verifySoftwareToken(code, 'quality.vet authenticator', {
      onSuccess: () =>
        user.setUserMfaPreference(null, { Enabled: true, PreferredMfa: true }, (error) =>
          error ? reject(error) : resolve(),
        ),
      onFailure: reject,
    }),
  );
}

export function AccountSecurityPanel({
  visible,
  pool,
  email,
  mfaEnabled,
  mfaRequired,
  embedded = false,
  accessToken,
  authority,
  onEnabled,
}: {
  visible: boolean;
  pool: CognitoUserPool | undefined;
  email?: string;
  mfaEnabled: boolean;
  mfaRequired: boolean;
  embedded?: boolean;
  accessToken?: string;
  authority?: string;
  onEnabled?: () => void;
}) {
  const [secret, setSecret] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [enabled, setEnabled] = useState(mfaEnabled);

  if (!visible) return null;

  const beginSetup = async () => {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (accessToken && authority) {
        setSecret(await associateSoftwareTokenWithAccessToken(authority, accessToken));
      } else {
        const user = currentCognitoUser(pool);
        await establishSession(user);
        setSecret(await associateToken(user));
      }
      setMessage('Add this secret to an authenticator app, then enter its six-digit code.');
    } catch (setupError) {
      setError(
        setupError instanceof Error ? setupError.message : 'MFA setup could not be started.',
      );
    } finally {
      setBusy(false);
    }
  };

  const completeSetup = async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      setError('Enter the six-digit code from your authenticator app.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (accessToken && authority) {
        await verifySoftwareTokenWithAccessToken(authority, accessToken, code.trim());
      } else {
        const user = currentCognitoUser(pool);
        await establishSession(user);
        await verifyToken(user, code.trim());
      }
      setEnabled(true);
      setSecret('');
      setCode('');
      setMessage('MFA is enabled. It will be required at your next sign-in.');
      onEnabled?.();
    } catch (verificationError) {
      setError(
        verificationError instanceof Error
          ? verificationError.message
          : 'The authentication code could not be verified.',
      );
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async () => {
    if (mfaRequired) return;
    setBusy(true);
    setError('');
    try {
      if (accessToken && authority) {
        await setSoftwareTokenMfa(authority, accessToken, false);
      } else {
        const user = currentCognitoUser(pool);
        await establishSession(user);
        await new Promise<void>((resolve, reject) =>
          user.setUserMfaPreference(
            null,
            { Enabled: false, PreferredMfa: false },
            (disableError) => (disableError ? reject(disableError) : resolve()),
          ),
        );
      }
      setEnabled(false);
      setMessage('MFA is disabled.');
    } catch (disableError) {
      setError(disableError instanceof Error ? disableError.message : 'MFA could not be disabled.');
    } finally {
      setBusy(false);
    }
  };

  const otpauth = secret
    ? `otpauth://totp/quality.vet:${encodeURIComponent(email ?? 'account')}?secret=${secret}&issuer=quality.vet`
    : '';

  const content = (
    <>
      <div className="workspace-heading">
        <div>
          <p className="eyebrow">Account security</p>
          <h2 id="account-security-title">Multi-factor authentication</h2>
        </div>
        <span>{enabled ? 'Enabled' : 'Not enabled'}</span>
      </div>
      <p>
        MFA is configured on your quality.vet account. Organisation requirements control access to
        that organisation only; they never reveal another organisation’s data.
      </p>
      {mfaRequired ? (
        <p className="security-required">
          Your selected organisation requires MFA. Complete setup before accessing its data.
        </p>
      ) : null}
      {!enabled && !secret ? (
        <button disabled={busy} type="button" onClick={() => void beginSetup()}>
          {busy ? 'Starting setup…' : 'Set up authenticator app'}
        </button>
      ) : null}
      {secret ? (
        <div className="mfa-setup">
          <label>
            Authenticator secret
            <input readOnly value={secret} />
          </label>
          <p>Manual setup URI: {otpauth}</p>
          <label>
            Six-digit verification code
            <input
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            />
          </label>
          <button disabled={busy} type="button" onClick={() => void completeSetup()}>
            {busy ? 'Verifying…' : 'Verify and enable MFA'}
          </button>
        </div>
      ) : null}
      {enabled && !mfaRequired ? (
        <button
          className="secondary-action"
          disabled={busy}
          type="button"
          onClick={() => void disableMfa()}
        >
          Disable MFA
        </button>
      ) : null}
      {message ? (
        <p aria-atomic="true" className="security-message" role="status" aria-live="polite">
          {message}
        </p>
      ) : null}
      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
    </>
  );

  return embedded ? (
    <div
      aria-labelledby="account-security-title"
      className="profile-setting-card account-security-panel mfa-profile-setting"
    >
      {content}
    </div>
  ) : (
    <section className="account-security-panel" aria-labelledby="account-security-title">
      {content}
    </section>
  );
}

export function TenantMfaPolicyPanel({
  visible,
  required,
  busy,
  onChange,
}: {
  visible: boolean;
  required: boolean;
  busy: boolean;
  onChange: (required: boolean) => Promise<void>;
}) {
  if (!visible) return null;
  return (
    <section className="tenant-security-panel" aria-labelledby="tenant-security-title">
      <div>
        <p className="eyebrow">Organisation security</p>
        <h2 id="tenant-security-title">Require MFA for this organisation</h2>
      </div>
      <p>
        Organisation Administrators can mandate MFA for every member of this organisation. This does
        not change MFA access rules for any other tenant.
      </p>
      <label className="security-toggle">
        <input
          checked={required}
          disabled={busy}
          type="checkbox"
          onChange={(event) => void onChange(event.target.checked)}
        />
        Require authenticator-app MFA for all organisation users
      </label>
    </section>
  );
}
